import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import * as Dialog from '@radix-ui/react-dialog';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { cn, FaIcon } from '@fe-learner-common';
import { DialogTitle } from '@radix-ui/react-dialog';

export interface ModalProps extends Dialog.DialogProps {
  className?: string;
  overlayClassName?: string;
  children: ReactNode;
  actions?: ReactNode;
  Trigger?: FC<Dialog.DialogTriggerProps>;
  TitleContent?: ReactNode;
  onClose?: () => void;
  closable?: boolean;
  container: HTMLElement;
}

export const Modal: FC<ModalProps> = ({
  className,
  overlayClassName,
  children,
  actions,
  onClose,
  closable = true,
  TitleContent,
  Trigger,
  container,
  ...rest
}) => {
  const intl = useIntl();

  return (
    <Dialog.Root {...rest}>
      {Trigger && <Trigger />}
      <Dialog.Portal container={container}>
        <Dialog.Overlay
          className={cn(
            'lf:data-[state=open]:animate-overlayShowFull lf:fixed lf:inset-0 lf:bg-black/10',
            overlayClassName,
          )}
        />
        <Dialog.Content
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={cn(
            'lf:data-[state=open]:animate-contentShow lf:fixed lf:left-6 lf:right-6 lf:top-[50%] lf:flex lf:max-h-[90vh] lf:translate-y-[-50%] lf:flex-col lf:rounded-2xl lf:bg-neutral-0  lf:shadow-100',
            'lf:md:left-[50%] lf:md:right-auto lf:md:translate-x-[-50%]',
            className,
          )}
        >
          {TitleContent || closable ? (
            <div
              className={cn('lf:flex lf:items-center lf:gap-x-3 lf:p-3 lf:ps-6', {
                'lf:border-b-1 lf:border-neutral-100': !!TitleContent,
              })}
            >
              <DialogTitle asChild>{TitleContent}</DialogTitle>

              {closable ? (
                <Dialog.Close asChild>
                  <button
                    className="lf:l-btn lf:l-btn-tertiary lf:l-btn-md lf:l-icon-btn lf:ms-auto"
                    aria-label={intl.formatMessage({
                      id: 'learner.common.close',
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onClose) {
                        onClose();
                      }
                    }}
                  >
                    <FaIcon icon={faXmark} width={20} height={20} />
                  </button>
                </Dialog.Close>
              ) : null}
            </div>
          ) : null}

          <div className="lf:h-full lf:overflow-auto">{children}</div>
          {actions ? (
            <div className="lf:flex lf:flex-wrap lf:justify-end lf:gap-2 lf:border-t-2 lf:border-neutral-100 lf:px-6 lf:py-4 ">
              {actions}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export const StatusModal: FC<ModalProps> = ({
  className,
  children,
  ...rest
}) => (
  <Modal className={cn(className, 'lf:sm:w-[720px]')} {...rest}>
    {children}
  </Modal>
);
